import axios from 'axios';
import { notify } from "@/main";
import { API } from '@/config/api';

const state = {
    transcriptions: [],
    currentTranscription: null,
    loading: false,
    error: null,
};

const mutations = {
    SET_TRANSCRIPTIONS(state, transcriptions) {
        state.transcriptions = transcriptions;
    },
    ADD_TRANSCRIPTIONS(state, transcriptions) {
        state.transcriptions = [...state.transcriptions, ...transcriptions];
    },
    SET_CURRENT_TRANSCRIPTION(state, transcription) {
        state.currentTranscription = transcription;
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    REMOVE_TRANSCRIPTION(state, transcriptionId) {
        state.transcriptions = state.transcriptions.filter(
            t => t.id !== transcriptionId
        );
    },
    REMOVE_TRANSLATION(state, { transcriptionId, translationId }) {
        const transcription = state.transcriptions.find(t => t.id === transcriptionId);
        if (transcription && transcription.translations) {
            transcription.translations = transcription.translations.filter(
                translation => translation.id !== translationId
            );
        }
    },
    UPDATE_PIECE(state, { pieceId, editedContent }) {
        // Update piece in transcriptions array
        state.transcriptions.forEach(transcription => {
            if (transcription.pieces) {
                const piece = transcription.pieces.find(p => p.id === pieceId);
                if (piece) {
                    piece.text = editedContent;
                }
            }
        });

        // Update piece in current transcription
        if (state.currentTranscription && state.currentTranscription.pieces) {
            const piece = state.currentTranscription.pieces.find(p => p.id === pieceId);
            if (piece) {
                piece.text = editedContent;
            }
        }
    },
    REMOVE_PIECE(state, { transcriptionId, pieceId }) {
        const transcription = state.transcriptions.find(t => t.id === transcriptionId);
        if (transcription && transcription.pieces) {
            transcription.pieces = transcription.pieces.filter(p => p.id !== pieceId);
        }

        if (state.currentTranscription && state.currentTranscription.id === transcriptionId) {
            state.currentTranscription.pieces = state.currentTranscription.pieces.filter(
                p => p.id !== pieceId
            );
        }
    },
};

const actions = {
    /**
     * Edit a piece of transcription
     * @param {Object} payload - { pieceId, editedContent }
     */
    async editPiece({ commit }, { pieceId, editedContent }) {
        try {
            const response = await axios.post(API.EDIT_PIECE, {
                pieceId,
                editedContent,
                token: "test",
            });

            commit('UPDATE_PIECE', { pieceId, editedContent });
            return {
                success: true,
                newTranscript: response.data?.result?.transcript,
            };
        } catch (error) {

            commit('SET_ERROR', error.message);
            return { success: false, error };
        }
    },

    /**
     * Delete a transcription piece
     * @param {Object} payload - { transcriptionId, pieceId, userId }
     */
    async deletePiece({ commit }, { transcriptionId, pieceId, userId }) {
        try {
            await axios.post(
                API.DELETE_PIECE,
                {
                    userId,
                    transcriptionId,
                    pieceId,
                    token: "test",
                }
            );

            commit('REMOVE_PIECE', { transcriptionId, pieceId });
            notify("You have successfully deleted this transcription", "success");
            return { success: true };
        } catch (error) {
            
            notify("Failed to delete transcription", "error");
            commit('SET_ERROR', error.message);
            return { success: false, error };
        }
    },

    /**
     * Delete a full transcription or translation
     * @param {Object} payload - { transcriptionId, userId }
     */
    async deleteTranscription({ commit }, { transcriptionId, userId }) {
        try {
            await axios.post(
                API.DELETE_TRANSLATION,
                {
                    userId,
                    transcriptionId,
                    token: "test",
                }
            );

            commit('REMOVE_TRANSCRIPTION', transcriptionId);
            notify("You have successfully deleted this transcription", "success");
            return { success: true };
        } catch (error) {
            
            notify("Failed to delete transcription", "error");
            commit('SET_ERROR', error.message);
            return { success: false, error };
        }
    },

    /**
     * Delete a translation
     * @param {Object} payload - { transcriptionId, translationId, userId }
     */
    async deleteTranslation({ commit }, { transcriptionId, translationId, userId }) {
        try {
            await axios.post(
                API.DELETE_TRANSLATION,
                {
                    userId,
                    transcriptionId: translationId,
                    token: "test",
                }
            );

            commit('REMOVE_TRANSLATION', { transcriptionId, translationId });
            notify("You have successfully deleted this translation", "success");
            return { success: true };
        } catch (error) {
            
            notify("Failed to delete translation", "error");
            commit('SET_ERROR', error.message);
            return { success: false, error };
        }
    },

    /**
     * Send transcription for translation
     * @param {Object} payload - { externalId, originalLanguage, translatedLanguage, userIds, ownerId }
     */
    async translateForTranscription({ commit }, { externalId, originalLanguage, translatedLanguage, userIds, ownerId }) {
        try {
            const userIdAsArray = typeof userIds === 'string' ? userIds : JSON.stringify(userIds);

            const data = {
                originalLanguage,
                translatedLanguage,
                externalId,
                userIds: userIdAsArray,
                ownerId,
            };

            const config = {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            };

            await axios.post(
                API.TRANSLATE_FOR_TRANSCRIPTION,
                data,
                config
            );

            notify("You have successfully sent your text for translation", "success");
            return { success: true };
        } catch (error) {
            
            notify("Failed to send translation", "error");
            commit('SET_ERROR', error.message);
            return { success: false, error };
        }
    },

    /**
     * Fetch transcriptions for a specific user
     * @param {Object} payload - { userId, limit, page, typeOfTranscription }
     */
    async fetchTranscriptionsForUser({ commit }, { userId, limit = 10, page = 1, typeOfTranscription }) {
        commit('SET_LOADING', true);

        try {
            const data = {
                userId,
                limit,
                page,
                token: "test",
                typeOfTranscription,
            };

            const response = await axios.post(
                API.GET_TRANSCRIPTIONS_FOR_USER,
                data
            );

            const transcriptions = response.data.result.transcriptions;

            if (page === 1) {
                commit('SET_TRANSCRIPTIONS', transcriptions);
            } else {
                commit('ADD_TRANSCRIPTIONS', transcriptions);
            }

            commit('SET_LOADING', false);
            return { success: true, data: transcriptions };
        } catch (error) {
            
            commit('SET_ERROR', error.message);
            commit('SET_LOADING', false);
            return { success: false, error };
        }
    },

    /**
     * Fetch transcriptions for a specific group
     * @param {Object} payload - { userId, roleId, groupId, limit, page, typeOfTranscription }
     */
    async fetchTranscriptionsForGroup({ commit }, { userId, roleId, groupId, limit = 10, page = 1, typeOfTranscription }) {
        commit('SET_LOADING', true);

        try {
            const response = await axios.post(
                API.GET_TRANSCRIPTIONS_FOR_GROUP,
                {
                    userId,
                    roleId,
                    groupId,
                    limit,
                    page,
                    token: "test",
                    typeOfTranscription,
                }
            );

            const transcriptions = response.data.result.transcriptions;

            if (page === 1) {
                commit('SET_TRANSCRIPTIONS', transcriptions);
            } else {
                commit('ADD_TRANSCRIPTIONS', transcriptions);
            }

            commit('SET_LOADING', false);
            return { success: true, data: transcriptions };
        } catch (error) {
            
            commit('SET_ERROR', error.message);
            commit('SET_LOADING', false);
            return { success: false, error };
        }
    },

    /**
     * Fetch transcriptions for a specific live/folder
     * @param {Object} payload - { nameOfLive, limit, page }
     */
    async fetchTranscriptionsOfLive({ commit }, { nameOfLive, limit = 5, page = 1 }) {
        commit('SET_LOADING', true);

        try {
            const data = {
                nameOfLive,
                page,
                limit,
                token: "test",
            };

            const response = await axios.post(
                API.GET_TRANSCRIPTIONS_OF_LIVE,
                data
            );

            const transcriptions = response.data.result.transcriptions;

            if (page === 1) {
                commit('SET_TRANSCRIPTIONS', transcriptions);
            } else {
                commit('ADD_TRANSCRIPTIONS', transcriptions);
            }

            commit('SET_LOADING', false);
            return { success: true, data: transcriptions };
        } catch (error) {
            
            commit('SET_ERROR', error.message);
            commit('SET_LOADING', false);
            return { success: false, error };
        }
    },

    /**
     * Translate subtitles to multiple languages
     * @param {Object} payload - { userId, externalId, companyId, languages, id, documentName, originalLanguage, wasabiUrl }
     */
    async translateSubtitles({ commit }, { userId, externalId, companyId, languages, id, documentName, originalLanguage, wasabiUrl }) {
        try {
            const formData = new FormData();
            formData.append('user_id', userId);
            formData.append('external_id', externalId);
            formData.append('publish_date', new Date().toISOString().split('T')[0]);
            formData.append('company_id', companyId || 1);

            // Languages should be an array of language codes
            const languagesArray = Array.isArray(languages)
                ? languages.map(lang => typeof lang === 'string' ? lang.toLowerCase() : lang)
                : [languages.toLowerCase()];

            formData.append('langs', JSON.stringify(languagesArray));
            formData.append('id', id);
            formData.append('document_name', documentName);
            formData.append('original_language', originalLanguage);
            formData.append('wasaby_url', wasabiUrl);

            await axios.post(
                API.TRANSLATE_SUBTITLES,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                }
            );

            notify(
                `You have successfully sent your text for translation to ${languagesArray.length} language(s)`,
                "success"
            );
            return { success: true };
        } catch (error) {
            
            notify("Failed to send subtitle translation", "error");
            commit('SET_ERROR', error.message);
            return { success: false, error };
        }
    },
};

const getters = {
    allTranscriptions: state => state.transcriptions,
    currentTranscription: state => state.currentTranscription,
    isLoading: state => state.loading,
    error: state => state.error,

    getTranscriptionById: state => id => {
        return state.transcriptions.find(t => t.id === id);
    },

    getTranscriptionsByType: state => type => {
        return state.transcriptions.filter(t => t.type === type);
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
